import { useQuery } from '@tanstack/react-query';
import {
  Drawer, Box, Typography, Stack, IconButton, Divider, Button, Chip, Alert,
  Skeleton, Grid, Paper,
} from '@mui/material';
import { Close, Edit, Block, LockOpen } from '@mui/icons-material';
import {
  USER_TYPE_LABELS,
  resolveAccountStatus,
  resolveBlockedReason,
  type UserRow,
} from '../../constants/userManagement';
import { getUserById } from '../../services/userService';

interface Props {
  open: boolean;
  user: UserRow | null;
  onClose: () => void;
  onEdit?: (row: UserRow) => void;
  onBlock?: (row: UserRow) => void;
  onUnblock?: (row: UserRow) => void;
}

function formatDateTime(iso?: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString();
}

function DetailRow({ label, value }: { label: string; value?: string | null }) {
  return (
    <Stack direction="row" justifyContent="space-between" spacing={2} sx={{ py: 0.75 }}>
      <Typography variant="body2" color="text.secondary" fontWeight={600} sx={{ flexShrink: 0 }}>
        {label}
      </Typography>
      <Typography variant="body2" fontWeight={700} sx={{ textAlign: 'right', wordBreak: 'break-word' }}>
        {value?.trim() || '—'}
      </Typography>
    </Stack>
  );
}

export default function UserDetailDrawer({
  open,
  user,
  onClose,
  onEdit,
  onBlock,
  onUnblock,
}: Props) {
  const { data: fullUser, isLoading, isError } = useQuery({
    queryKey: ['admin-user-detail', user?._id],
    queryFn: () => getUserById(user!._id),
    enabled: open && !!user?._id,
  });

  const row = (fullUser || user) as UserRow | null;
  if (!open || !user) return null;

  const account = resolveAccountStatus(row || user);
  const name = `${row?.firstName || user.firstName || ''} ${row?.lastName || user.lastName || ''}`.trim() || 'User';
  const typeLabel = USER_TYPE_LABELS[row?.userType || user.userType || ''] || row?.userType || user.userType || '—';
  const blockedReason = row?.isBlocked || user.isBlocked
    ? resolveBlockedReason(row || user)
    : '';

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      ModalProps={{ keepMounted: false }}
      slotProps={{
        root: { sx: { zIndex: (t) => t.zIndex.modal + 4 } },
      }}
      PaperProps={{ sx: { width: { xs: '100%', sm: 440, md: 480 } } }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', bgcolor: 'background.paper' }}>
        <Stack direction="row" alignItems="flex-start" spacing={1} sx={{ p: 2.5, borderBottom: 1, borderColor: 'divider' }}>
          <Box flex={1} minWidth={0}>
            <Typography variant="h6" fontWeight={800}>{name}</Typography>
            <Typography variant="body2" color="text.secondary">
              Account details
            </Typography>
          </Box>
          <IconButton size="small" onClick={onClose} aria-label="Close">
            <Close />
          </IconButton>
        </Stack>

        <Box sx={{ flex: 1, overflow: 'auto', p: 2.5 }}>
          {isLoading && !fullUser && (
            <Stack spacing={1} sx={{ mb: 2 }}>
              <Skeleton height={28} />
              <Skeleton height={28} />
              <Skeleton height={80} />
            </Stack>
          )}

          {isError && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Could not refresh from server — showing list data.
            </Alert>
          )}

          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
            <Chip size="small" label={typeLabel} variant="outlined" sx={{ fontWeight: 700 }} />
            <Chip size="small" label={account.label} color={account.color} sx={{ fontWeight: 800 }} />
            {!(row?.isVerified ?? user.isVerified) && (
              <Chip size="small" label="Unverified" color="warning" variant="outlined" />
            )}
          </Stack>

          <Alert severity={account.key === 'blocked' ? 'error' : 'info'} sx={{ mb: 2 }}>
            {account.detail}
          </Alert>

          <Paper variant="outlined" sx={{ p: 1.5, mb: 2, borderRadius: 2 }}>
            <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 0.5 }}>Profile</Typography>
            <DetailRow label="Name" value={name} />
            <Divider />
            <DetailRow label="Email" value={row?.email || user.email} />
            <Divider />
            <DetailRow label="Phone" value={row?.phone || user.phone} />
            <Divider />
            <DetailRow label="Account type" value={typeLabel} />
            <Divider />
            <DetailRow label="Account status" value={account.label} />
            {account.key === 'blocked' && (
              <>
                <Divider />
                <DetailRow label="Blocked reason" value={blockedReason} />
              </>
            )}
            <Divider />
            <DetailRow label="Verified" value={(row?.isVerified ?? user.isVerified) ? 'Yes' : 'No'} />
            <Divider />
            <DetailRow label="Registered" value={formatDateTime(row?.createdAt || user.createdAt)} />
            <Divider />
            <DetailRow label="Last login" value={formatDateTime(row?.lastLogin || user.lastLogin)} />
            {(row?.lastLoginIp || user.lastLoginIp) && (
              <>
                <Divider />
                <DetailRow label="Last login IP" value={row?.lastLoginIp || user.lastLoginIp} />
              </>
            )}
          </Paper>

          {(row?.metadata || user.metadata) && (
            <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
              <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 0.5 }}>Address & notes</Typography>
              <DetailRow label="City" value={String((row?.metadata || user.metadata)?.city || '')} />
              <Divider />
              <DetailRow label="Address" value={String((row?.metadata || user.metadata)?.address || '')} />
              <Divider />
              <DetailRow
                label="Admin notes"
                value={String((row?.metadata || user.metadata)?.adminNotes || (row?.metadata || user.metadata)?.notes || '')}
              />
            </Paper>
          )}
        </Box>

        <Divider />
        <Grid container spacing={1} sx={{ p: 2 }}>
          <Grid item xs={12} sm="auto">
            <Button fullWidth variant="outlined" onClick={onClose}>Close</Button>
          </Grid>
          {onEdit && row && (
            <Grid item xs={12} sm>
              <Button
                fullWidth
                variant="contained"
                startIcon={<Edit />}
                onClick={() => onEdit(row)}
              >
                Edit
              </Button>
            </Grid>
          )}
          {row?.isBlocked && onUnblock && (
            <Grid item xs={12}>
              <Button
                fullWidth
                color="success"
                variant="outlined"
                startIcon={<LockOpen />}
                onClick={() => onUnblock(row)}
              >
                Unblock
              </Button>
            </Grid>
          )}
          {row && !row.isBlocked && onBlock && (
            <Grid item xs={12}>
              <Button
                fullWidth
                color="warning"
                variant="outlined"
                startIcon={<Block />}
                onClick={() => onBlock(row)}
              >
                Block
              </Button>
            </Grid>
          )}
        </Grid>
      </Box>
    </Drawer>
  );
}
